import { sv } from 'date-fns/locale';
import React, { useCallback, useMemo, useState } from 'react';

import { GeodataProcessType } from '../../../../typings/api/skymap/rest/v0/.common';
import { GcpRow } from '../../react/components/cloud-processing/gcp-parser';
import {
  GeodataConfigurationData,
  geodataGsdLimits,
} from '../../react/components/cloud-processing/geodata-configuration/geodata-configuration';
import { AddOnData } from '../../react/components/cloud-processing/new-cloud-process-dialog/add-ons';
import { formattedDate } from '../../utils/dateUtils';
import { useGsdUnit } from '../hooks/use-gsd-unit';

export type GeodataSetupPage =
  | 'processType'
  | 'settings'
  | 'gcp'
  | 'boundary'
  | 'images'
  | 'addons'
  | 'summary'
  | 'finish';

export type GeodataSetupContextType = {
  page: GeodataSetupPage;
  setPage: (page: GeodataSetupPage) => void;
  closeDialog?: () => void;
  selectedPackage: GeodataProcessType;
  setSelectedPackage: (packageType: GeodataProcessType) => void;
  geodataConfiguration: GeodataConfigurationData;
  setGeodataConfiguration: (data: GeodataConfigurationData) => void;
  addons: AddOnData | undefined;
  setAddons: (addons: AddOnData | undefined) => void;
  gcpPoints: GcpRow[];
  setGcpPoints: (points: GcpRow[]) => void;
  gcpFilename: string | undefined;
  setGcpFilename: (filename: string | undefined) => void;
  setGcp: (data: { filename: string; gcpPoints: GcpRow[] }) => void;
  boundaryFiles: { filename: string; file: File }[];
  setBoundaryFiles: (files: { filename: string; file: File }[]) => void;
  imageFiles: { filename: string; file: File; imageIdentifier: string }[];
  setImageFiles: (files: { filename: string; file: File; imageIdentifier: string }[]) => void;
  skipGcpStep: boolean;
  setSkipGcpStep: (skip: boolean) => void;
  useInteractiveSetup: boolean;
  setUseInteractiveSetup: (use: boolean) => void;
  disableNextButton: boolean;
  gcpEnabled: boolean;
  addonsEnabled: boolean;
  setSettingsValid: (valid: boolean) => void;
  isImportingGcpFile: boolean;
  setIsImportingGcpFile: (value: boolean) => void;
  disableInteractiveSetup: boolean;
  setDisableInteractiveSetup: (value: boolean) => void;
  isFinishComplete: boolean;
  setIsFinishComplete: (value: boolean) => void;
  abortConfirmed: boolean;
  setAbortConfirmed: (value: boolean) => void;
};

type Props = {
  children: React.ReactNode;
  closeDialog?: () => void;
};

export const GeodataSetupContext = React.createContext<GeodataSetupContextType | undefined>(
  undefined,
);

export const GeodataSetupState = (props: Props) => {
  const [selectedPackage, setSelectedPackage] = useState<GeodataProcessType>('FULL');
  const [page, setPage] = useState<GeodataSetupPage>('processType');
  const gsdUnit = useGsdUnit();
  const [geodataConfiguration, setGeodataConfiguration] = useState<GeodataConfigurationData>({
    name: formattedDate(Date.now(), sv),
    objMaxTriangles: undefined,
    gsd: geodataGsdLimits[gsdUnit].defaultValue,
    rollingShutter: false,
  });
  const [settingsValid, setSettingsValid] = useState<boolean>(true);
  const [_addons, setAddons] = useState<AddOnData | undefined>(undefined);
  const [_gcpPoints, setGcpPoints] = useState<GcpRow[]>([]);
  const [disableInteractiveSetup, setDisableInteractiveSetup] = useState<boolean>(false);
  const [gcpFilename, setGcpFilename] = useState<string | undefined>(undefined);
  const [boundaryFiles, setBoundaryFiles] = useState<{ filename: string; file: File }[]>([]);
  const [imageFiles, setImageFiles] = useState<GeodataSetupContextType['imageFiles']>([]);
  const [_skipGcpStep, setSkipGcpStep] = useState<boolean>(false);
  const [_useInteractiveSetup, setUseInteractiveSetup] = useState<boolean>(false);
  const [isImportingGcpFile, setIsImportingGcpFile] = useState<boolean>(false);
  const [isFinishComplete, setIsFinishComplete] = useState<boolean>(false);
  const [abortConfirmed, setAbortConfirmed] = useState<boolean>(false);

  const setGcp = useCallback((data: { filename: string; gcpPoints: GcpRow[] }) => {
    setGcpPoints(data.gcpPoints);
    setGcpFilename(data.filename.trim().length > 0 ? data.filename : 'gcp.txt');
  }, []);

  const disableNextButton =
    (page === 'gcp' && isImportingGcpFile) ||
    (page === 'images' && imageFiles.length < 3) ||
    (page === 'settings' && !settingsValid);

  const gcpEnabled = selectedPackage !== 'ORTHO_ONLY';

  // Addons are not available for the PM process type.
  const addonsEnabled = gcpEnabled && selectedPackage !== 'PM';

  // Only allow GCP points if GCP is enabled
  const gcpPoints = useMemo(() => {
    return gcpEnabled ? _gcpPoints : [];
  }, [_gcpPoints, gcpEnabled]);

  const addons = useMemo(() => {
    return addonsEnabled ? _addons : undefined;
  }, [_addons, addonsEnabled]);

  // Always skip GCP step if GCP is disabled.
  // Not possible to skip GCP adjustment if we have the addon activated.
  const skipGcpStep =
    addons?.gcpAdjustment?.enabled === true ? false : gcpEnabled ? _skipGcpStep : true;

  // Only allow useInteractiveSetup if GCP is enabled and GCP-adjustment addon is not activated.
  const useInteractiveSetup =
    _useInteractiveSetup &&
    gcpEnabled &&
    addons?.gcpAdjustment?.enabled !== true &&
    imageFiles.length === 0;

  const contextValue = useMemo<GeodataSetupContextType>(() => {
    return {
      selectedPackage,
      setSelectedPackage,
      page,
      setPage,
      closeDialog: props.closeDialog,
      geodataConfiguration,
      setGeodataConfiguration,
      addons,
      setAddons,
      gcpPoints,
      setGcpPoints,
      gcpFilename,
      setGcpFilename,
      setGcp,
      boundaryFiles,
      setBoundaryFiles,
      imageFiles,
      setImageFiles,
      skipGcpStep,
      setSkipGcpStep,
      useInteractiveSetup,
      setUseInteractiveSetup,
      disableNextButton,
      gcpEnabled,
      addonsEnabled,
      setSettingsValid,
      isImportingGcpFile,
      setIsImportingGcpFile,
      disableInteractiveSetup,
      setDisableInteractiveSetup,
      isFinishComplete,
      setIsFinishComplete,
      abortConfirmed,
      setAbortConfirmed,
    };
  }, [
    selectedPackage,
    page,
    geodataConfiguration,
    addons,
    gcpPoints,
    gcpFilename,
    boundaryFiles,
    imageFiles,
    skipGcpStep,
    useInteractiveSetup,
    props.closeDialog,
    disableNextButton,
    setGcp,
    gcpEnabled,
    addonsEnabled,
    setSettingsValid,
    isImportingGcpFile,
    disableInteractiveSetup,
    setDisableInteractiveSetup,
    isFinishComplete,
    abortConfirmed,
  ]);

  return (
    <GeodataSetupContext.Provider value={contextValue}>
      {props.children}
    </GeodataSetupContext.Provider>
  );
};
